// WALLET-P1 (docs/design/2026-10-27-wallet-pricing.md §6, §7, §10 phase 1): the prepaid AI
// credits wallet. Three surfaces:
//
// - the client's (Setup › Company › Billing), `/v1/companies/{co}/wallet…`: owners and admins
//   (and an agency operator, reading) see the balance, history and spend in dollars, add funds
//   through Stripe Checkout (never an operator: an operator credits by adjustment) and set the
//   warning level. Members and client viewers get 404. No client contract carries our real cost
//   or a model name; tokens appear only in the entry detail;
// - the operator's (Operator › Billing), `/v1/operator/…`: the platform tariff and the bonus
//   ladder, and per company the switch and limits, a custom tariff, manual credit, refunds, and
//   the margin;
// - Stripe's, `POST /v1/stripe/webhook`: no principal, the `t=,v1=` signature is the credential.
//
// Money is integer micros of USD (1 USD = 1,000,000 micros); clients see it rounded to cents.
import { z } from "zod";
import { TariffScope, WalletCheckoutStatus, WalletEntryKind } from "../enums";
import { IsoDate, MicrosAmount, MonthKey, Slug, Timestamp, Uuid } from "../primitives";
import {
  CatalogTariff,
  OperatorWalletEntry,
  Tariff,
  TariffCategoryMarkup,
  TariffModelPrice,
  TariffPlanPrice,
  TariffUnitPrice,
  TopupBonusTier,
  WalletEntry,
  WalletUnits,
} from "../wallet-rows";
import { CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const op = "/v1/operator";
const wallet = { feature: "core", tag: "wallet" } as const;

const Bp = z.int().min(0).max(10_000);
const PositiveMicros = z.int().min(1);

// Views -----------------------------------------------------------------------------------------

/** The rates in effect for the company (its override, else the platform list). Model-blind. */
export const WalletTariffView = z
  .object({
    scope: TariffScope,
    version: z.int(),
    label: z.string(),
    effective_from: IsoDate,
    input_per_m_micros: MicrosAmount,
    output_per_m_micros: MicrosAmount,
    image_micros: MicrosAmount,
    /** Per-unit prices (video analysed, audio transcribed, speech, web search, …). */
    unit_prices: z.array(TariffUnitPrice),
    reported_cost_markup_bp: z.int(),
  })
  .meta({ id: "WalletTariffView" });

/** A rung of the bonus ladder as the top-up modal shows it ("$500 +10 %"). */
export const WalletLadderRung = z
  .object({ min_amount_micros: MicrosAmount, bonus_bp: z.int(), label: z.string().nullable() })
  .meta({ id: "WalletLadderRung" });

/**
 * The company's wallet. `available` = balance − what live runs hold (their reservations);
 * `low_threshold_micros` = `low_watermark_bp` of the last top-up, never below $5. A balance can
 * end a few cents below zero (gateway spend is read after the fact): clients see $0.00.
 */
export const WalletView = z
  .object({
    enabled: z.boolean(),
    currency: z.literal("USD"),
    balance_micros: MicrosAmount,
    held_micros: MicrosAmount,
    available_micros: MicrosAmount,
    spent_this_month_micros: MicrosAmount,
    last_topup_micros: MicrosAmount.nullable(),
    low_watermark_bp: Bp,
    low_threshold_micros: MicrosAmount,
    min_topup_micros: MicrosAmount,
    max_topup_micros: MicrosAmount,
    paused: z.boolean(),
    paused_at: Timestamp.nullable(),
    tariff: WalletTariffView.nullable(),
    ladder: z.array(WalletLadderRung),
    /** False until Stripe is configured (`STRIPE_SECRET_KEY`): Add funds says so. */
    checkout_ready: z.boolean(),
    /**
     * The top-up named by `?checkout=` (the page Stripe sends the client back to): `paid` once
     * the webhook credited it, `open` while it has not; null without the parameter or when the
     * company has no such top-up.
     */
    checkout: z
      .object({
        id: Uuid,
        status: WalletCheckoutStatus,
        amount_micros: MicrosAmount,
        bonus_micros: MicrosAmount,
        completed_at: Timestamp.nullable(),
      })
      .nullable(),
  })
  .meta({ id: "WalletView" });

export const WalletGetQuery = z.strictObject({ checkout: Uuid.optional() }).meta({
  id: "WalletGetQuery",
});
export type WalletView = z.infer<typeof WalletView>;

/**
 * One line of the History: a money movement, or an agent run's usage grouped into one line
 * (`entries` = the calls in it; usage without a run is grouped per day). `id` is the newest
 * entry of the line. `?run=` lists a run's own calls, one line each (the detail drawer).
 * Lines are newest first in the order the ledger moved (its posting sequence).
 * `balance_after_micros` is the balance right after a single entry; null on a line that groups
 * several, whose movements interleave with other lines.
 */
export const WalletLine = z
  .object({
    id: Uuid,
    kind: WalletEntryKind,
    label: z.string(),
    occurred_at: Timestamp,
    amount_micros: MicrosAmount,
    balance_after_micros: MicrosAmount.nullable(),
    entries: z.int().min(1),
    run_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    task_title: z.string().nullable(),
    membership_id: Uuid.nullable(),
    agent_name: z.string().nullable(),
    units: WalletUnits.nullable(),
    receipt_url: z.string().nullable(),
  })
  .meta({ id: "WalletLine" });
export type WalletLine = z.infer<typeof WalletLine>;

export const WalletEntriesQuery = PageQuery.extend({
  kind: WalletEntryKind.optional(),
  from: Timestamp.optional().describe("inclusive"),
  to: Timestamp.optional().describe("exclusive"),
  run: Uuid.optional().describe("one run's usage, one line per call (no grouping)"),
  day: IsoDate.optional().describe(
    "one UTC day's usage outside any run, one line per call (no grouping)",
  ),
  spend_group: z
    .enum(["agent", "task", "day", "category"])
    .optional()
    .describe("with spend_key, from and to: one Spend row's charges, one line per call"),
  spend_key: z
    .string()
    .max(64)
    .optional()
    .describe("the Spend row's key (empty: no agent or no task)"),
});

export const WalletSpendGroup = z.enum(["agent", "task", "day", "category"]);
export const WalletSpendQuery = z.object({
  period: MonthKey.optional().describe("default: the current month (UTC)"),
  group: WalletSpendGroup.optional().describe("default: agent"),
});

/** Charged usage of one agent, task or day, in dollars (micros), with the units behind it. */
export const WalletSpendRow = z
  .object({
    key: z.string(),
    label: z.string(),
    charged_micros: MicrosAmount,
    runs: z.int().min(0),
    tokens_in: z.int().min(0),
    tokens_out: z.int().min(0),
    images: z.int().min(0),
  })
  .meta({ id: "WalletSpendRow" });

export const WalletSpend = z
  .object({
    period: MonthKey,
    group: WalletSpendGroup,
    total_micros: MicrosAmount,
    rows: z.array(WalletSpendRow),
  })
  .meta({ id: "WalletSpend" });

export const WalletTopUpCreate = z
  .strictObject({ amount_micros: PositiveMicros })
  .meta({ id: "WalletTopUpCreate" });

/** The Checkout Session to send the browser to; credit lands when Stripe says it is paid. */
export const WalletTopUp = z
  .object({
    checkout_id: Uuid,
    checkout_url: z.url(),
    amount_micros: MicrosAmount,
    bonus_micros: MicrosAmount,
    bonus_bp: Bp,
    tier_label: z.string().nullable(),
  })
  .meta({ id: "WalletTopUp" });

/** The client's own setting (auto-reload is phase 2: any other field is refused). */
export const WalletSettingsPatch = z
  .strictObject({ low_watermark_bp: Bp })
  .meta({ id: "WalletSettingsPatch" });

export const StripeWebhookAck = z
  .object({ received: z.literal(true) })
  .meta({ id: "StripeWebhookAck" });

// Operator ----------------------------------------------------------------------------------------

const Rates = {
  input_per_m_micros: z.int().min(0),
  output_per_m_micros: z.int().min(0),
  image_micros: z.int().min(0),
  /** Omitted: the known units at their launch prices (KNOWN_TARIFF_UNITS). Units are unique. */
  unit_prices: z
    .array(TariffUnitPrice)
    .max(50)
    .refine((l) => new Set(l.map((u) => u.unit)).size === l.length, "each unit once")
    .optional(),
  reported_cost_markup_bp: z.int().min(0).max(100_000).optional(),
  /** Markups of reported spend per category that has no unit price. Categories are unique. */
  category_markups: z
    .array(TariffCategoryMarkup)
    .max(50)
    .refine((l) => new Set(l.map((c) => c.category)).size === l.length, "each category once")
    .optional(),
  /** HTVA-RUN-FIXES: explicit client prices per model. Models are unique. */
  model_prices: z
    .array(TariffModelPrice)
    .max(100)
    .refine((l) => new Set(l.map((m) => m.model)).size === l.length, "each model once")
    .optional(),
  /** HTVA-RUN-FIXES: a model without an explicit price costs the client cost × (1 + markup). */
  cost_markup_bp: z.int().min(0).max(100_000).nullable().optional(),
  /**
   * OPERATOR-CONSOLE: per-plan client prices (a plan's own model price, or its own markup). One
   * line per (plan, model); only on the platform list (a company's own version is already its own).
   */
  plan_prices: z
    .array(TariffPlanPrice)
    .max(200)
    .refine(
      (l) => new Set(l.map((p) => `${p.plan_key}|${p.model ?? ""}`)).size === l.length,
      "each plan and model once",
    )
    .optional(),
};

/** A new tariff version: its rates and the day it takes effect (today or later). */
export const TariffCreate = z
  .strictObject({
    label: z.string().trim().min(1).max(60),
    effective_from: IsoDate,
    ...Rates,
  })
  .meta({ id: "TariffCreate" });

/** A platform version with whether it may still be retired (not yet in effect). */
export const CatalogTariffRow = CatalogTariff.extend({ retirable: z.boolean() }).meta({
  id: "CatalogTariffRow",
});

export const TopupBonusTierCreate = z
  .strictObject({
    min_amount_micros: PositiveMicros,
    bonus_bp: Bp,
    label: z.string().trim().min(1).max(60).nullable().optional(),
    effective_from: IsoDate.optional(),
  })
  .meta({ id: "TopupBonusTierCreate" });

export const TopupBonusTierPatch = z
  .strictObject({
    min_amount_micros: PositiveMicros.optional(),
    bonus_bp: Bp.optional(),
    label: z.string().trim().min(1).max(60).nullable().optional(),
  })
  .meta({ id: "TopupBonusTierPatch" });

const OperatorCompanyParams = z.object({ company_id: Uuid });
const TariffParams = z.object({ tariff_id: Uuid });
const TierParams = z.object({ tier_id: Uuid });
const CompanyTariffParams = z.object({ company_id: Uuid, tariff_id: Uuid });

const CompanyRef = z.object({ id: Uuid, slug: Slug, name: z.string() });

/** OK, low (at the warning level), paused (no credit), off (wallet not switched on). */
export const WalletStatus = z.enum(["ok", "low", "paused", "off"]);

/** Charged, our cost and the margin of a period (usage entries only). */
const MarginFigures = {
  charged_micros: MicrosAmount,
  cost_micros: MicrosAmount,
  margin_micros: MicrosAmount,
};

/** One company's wallet as the operator sees it: the client's view plus cost and margin. */
export const OperatorWalletView = z
  .object({
    company: CompanyRef,
    /** HTVA's own company: the wallet stays off there (Andrei, 2026-10-27). */
    home_company: z.boolean(),
    status: WalletStatus,
    wallet: WalletView,
    month: z.object({ key: MonthKey, ...MarginFigures }),
    tariffs: z.array(Tariff),
    recent: z.array(OperatorWalletEntry),
    checkouts: z.array(
      z.object({
        id: Uuid,
        amount_micros: MicrosAmount,
        bonus_micros: MicrosAmount,
        refunded_micros: MicrosAmount,
        completed_at: Timestamp,
      }),
    ),
    /**
     * Refund requests not settled yet (Stripe's answer was lost or is awaited): each holds its
     * amount from the credit. Resume = the same request again under its `request_key`.
     */
    pending_refunds: z.array(
      z.object({
        request_key: Uuid,
        checkout_id: Uuid,
        amount_micros: MicrosAmount,
        hold_micros: MicrosAmount,
        reason: z.string(),
        created_at: Timestamp,
      }),
    ),
  })
  .meta({ id: "OperatorWalletView" });

export const OperatorWalletSettingsPut = z
  .strictObject({
    enabled: z.boolean(),
    min_topup_micros: PositiveMicros,
    max_topup_micros: PositiveMicros,
  })
  .meta({ id: "OperatorWalletSettingsPut" });

export const WalletAdjustmentCreate = z
  .strictObject({
    amount_micros: z
      .int()
      .refine((v) => v !== 0, "an adjustment moves money")
      .refine((v) => Math.abs(v) <= 10_000_000_000, "at most $10,000 per adjustment"),
    reason: z.string().trim().min(1).max(500),
  })
  .meta({ id: "WalletAdjustmentCreate" });

export const WalletRefundCreate = z
  .strictObject({
    checkout_id: Uuid,
    amount_micros: PositiveMicros,
    reason: z.string().trim().min(1).max(500),
  })
  .meta({ id: "WalletRefundCreate" });

/**
 * BILLING-LEDGER: an operator records a top-up's payment on the ledger: the client's waiting one
 * (`checkout_id`), or a new one of `amount_micros` (the same range, bonus ladder and credit as a
 * card top-up).
 */
export const WalletLedgerTopUp = z
  .strictObject({
    checkout_id: Uuid.optional(),
    amount_micros: PositiveMicros.optional(),
    method: z.enum(["card", "us_bank_account", "customer_balance"]).default("customer_balance"),
  })
  .refine((b) => (b.checkout_id === undefined) !== (b.amount_micros === undefined), {
    message: "either checkout_id or amount_micros",
  })
  .meta({ id: "WalletLedgerTopUp" });

export const WalletMarginQuery = z.object({
  month: MonthKey.optional().describe("default: the current month (UTC)"),
});

const MarginLine = z.object({ key: z.string(), label: z.string(), ...MarginFigures });

export const WalletMargin = z
  .object({
    month: MonthKey,
    complete: z.boolean(),
    incomplete_reasons: z.array(z.string()),
    companies: z.array(
      z.object({
        company: CompanyRef,
        status: WalletStatus,
        balance_micros: MicrosAmount,
        spent_this_month_micros: MicrosAmount,
        ...MarginFigures,
        agents: z.array(MarginLine),
        tasks: z.array(MarginLine),
      }),
    ),
  })
  .meta({ id: "WalletMargin" });

// Routes ------------------------------------------------------------------------------------------

export const walletRoutes = {
  "wallet.get": defineRoute({
    ...wallet,
    id: "wallet.get",
    method: "get",
    path: `${base}/wallet`,
    summary: "The company's credit: balance, what is held, prices, the bonus ladder",
    access: "manager",
    params: CompanyParams,
    query: WalletGetQuery,
    response: { status: 200, schema: WalletView },
    errors: [],
    emits: [],
  }),
  "wallet.entries": defineRoute({
    ...wallet,
    id: "wallet.entries",
    method: "get",
    path: `${base}/wallet/entries`,
    summary: "History: money in and out, a run's usage as one line (newest first)",
    access: "manager",
    params: CompanyParams,
    query: WalletEntriesQuery,
    response: { status: 200, schema: page(WalletLine) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "wallet.entry": defineRoute({
    ...wallet,
    id: "wallet.entry",
    method: "get",
    path: `${base}/wallet/entries/{id}`,
    summary: "One entry with its units (tokens appear only here)",
    access: "manager",
    params: z.object({ co: Uuid, id: Uuid }),
    response: { status: 200, schema: WalletEntry },
    errors: [],
    emits: [],
  }),
  "wallet.spend": defineRoute({
    ...wallet,
    id: "wallet.spend",
    method: "get",
    path: `${base}/wallet/spend`,
    summary: "What agents spent in a month, per agent, task, day or category, in dollars",
    access: "manager",
    params: CompanyParams,
    query: WalletSpendQuery,
    response: { status: 200, schema: WalletSpend },
    errors: [],
    emits: [],
  }),
  "wallet.topUp": defineRoute({
    ...wallet,
    id: "wallet.topUp",
    method: "post",
    path: `${base}/wallet/top-ups`,
    summary: "Add funds: a Stripe Checkout Session (an operator credits by adjustment instead)",
    access: "manager",
    params: CompanyParams,
    body: WalletTopUpCreate,
    idempotency: "optional",
    response: { status: 201, schema: WalletTopUp },
    errors: ["wallet_off", "not_ready"],
    emits: ["wallet.checkout_started"],
  }),
  "wallet.updateSettings": defineRoute({
    ...wallet,
    id: "wallet.updateSettings",
    method: "patch",
    path: `${base}/wallet/settings`,
    summary: "Set the low-balance warning level",
    access: "manager",
    params: CompanyParams,
    body: WalletSettingsPatch,
    response: { status: 200, schema: WalletView },
    errors: [],
    emits: ["wallet.settings_changed", "wallet.low"],
  }),
  "stripe.webhook": defineRoute({
    ...wallet,
    id: "stripe.webhook",
    method: "post",
    path: "/v1/stripe/webhook",
    summary: "Stripe events (checkout paid, charge refunded); signature-verified, idempotent",
    description:
      "Stripe's event, verified against `STRIPE_WEBHOOK_SECRET` (`Stripe-Signature: t=,v1=`, " +
      "HMAC-SHA256 of `<t>.<raw body>`, 300 s window) before anything is read; a bad or stale " +
      "signature is 401. `checkout.session.completed` credits the top-up and its bonus once " +
      "(meter `stripe:cs:{session}`) and books any refund Stripe already made; " +
      "`charge.refunded`, `charge.refund.updated`, `refund.updated` and `refund.failed` read " +
      "the payment's refunds back from Stripe: each succeeded or pending one is booked once " +
      "(meter `stripe:re:{refund}`) with the bonus clawed back pro rata, and a booked one that " +
      "failed or was cancelled is given back (meter `stripe:re:{refund}:failed`, " +
      "`wallet.adjusted`); any other type is acknowledged and ignored. Bodies over 256 KiB are " +
      "413.",
    access: "stripe_signature",
    body: z.unknown(),
    response: { status: 200, schema: StripeWebhookAck },
    // not_ready (503): Stripe not set up, or (BILLING-PLANS) a billing object naming the company
    // whose mapping is not committed yet; Stripe delivers it again
    errors: ["not_ready"],
    emits: [
      "wallet.credited",
      "wallet.refunded",
      "wallet.adjusted",
      "wallet.low",
      "wallet.empty",
      // BILLING-PLANS (docs/design/2026-10-27-wallet-pricing.md §3.5–§3.7): the invoice mirror,
      // the subscription's state and dunning, quotes accepted or canceled in Stripe.
      "invoice.created",
      "invoice.finalized",
      "invoice.paid",
      "invoice.payment_failed",
      "invoice.voided",
      "invoice.marked_uncollectible",
      "subscription.updated",
      "subscription.past_due",
      "subscription.restored",
      "subscription.cancelled",
      // the card on file (brand and last 4) after a Portal save
      "billing.account_changed",
      "digital_employee.resumed",
      "quote.changed",
      // `project.changed` (a quote accepted for a build) comes back with BILLING-ONEOFF
      "de_maintenance.changed",
      "cost.posted",
    ],
  }),

  "operator.tariffs.list": defineRoute({
    ...wallet,
    id: "operator.tariffs.list",
    method: "get",
    path: `${op}/tariffs`,
    summary: "The platform tariff's versions, newest first",
    access: "operator",
    response: { status: 200, schema: z.object({ data: z.array(CatalogTariffRow) }) },
    errors: [],
    emits: [],
  }),
  "operator.tariffs.create": defineRoute({
    ...wallet,
    id: "operator.tariffs.create",
    method: "post",
    path: `${op}/tariffs`,
    summary: "A new platform tariff version (cells get it from the mirror job)",
    access: "operator",
    body: TariffCreate,
    idempotency: "optional",
    response: { status: 201, schema: CatalogTariffRow },
    errors: ["conflict"],
    emits: ["tariff_catalog.changed"],
  }),
  "operator.tariffs.retire": defineRoute({
    ...wallet,
    id: "operator.tariffs.retire",
    method: "delete",
    path: `${op}/tariffs/{tariff_id}`,
    summary: "Retire a platform version that is not in effect yet",
    access: "operator",
    params: TariffParams,
    response: { status: 200, schema: CatalogTariffRow },
    errors: ["conflict"],
    emits: ["tariff_catalog.changed"],
  }),
  "operator.topupBonusTiers.list": defineRoute({
    ...wallet,
    id: "operator.topupBonusTiers.list",
    method: "get",
    path: `${op}/topup-bonus-tiers`,
    summary: "The top-up bonus ladder",
    access: "operator",
    response: { status: 200, schema: z.object({ data: z.array(TopupBonusTier) }) },
    errors: [],
    emits: [],
  }),
  "operator.topupBonusTiers.create": defineRoute({
    ...wallet,
    id: "operator.topupBonusTiers.create",
    method: "post",
    path: `${op}/topup-bonus-tiers`,
    summary: "Add a rung to the bonus ladder",
    access: "operator",
    body: TopupBonusTierCreate,
    idempotency: "optional",
    response: { status: 201, schema: TopupBonusTier },
    errors: ["conflict"],
    emits: ["topup_bonus_tier.changed"],
  }),
  "operator.topupBonusTiers.update": defineRoute({
    ...wallet,
    id: "operator.topupBonusTiers.update",
    method: "patch",
    path: `${op}/topup-bonus-tiers/{tier_id}`,
    summary: "Edit a rung of the bonus ladder",
    access: "operator",
    params: TierParams,
    body: TopupBonusTierPatch,
    response: { status: 200, schema: TopupBonusTier },
    errors: ["conflict"],
    emits: ["topup_bonus_tier.changed"],
  }),
  "operator.topupBonusTiers.delete": defineRoute({
    ...wallet,
    id: "operator.topupBonusTiers.delete",
    method: "delete",
    path: `${op}/topup-bonus-tiers/{tier_id}`,
    summary: "Remove a rung of the bonus ladder",
    access: "operator",
    params: TierParams,
    response: { status: 200, schema: TopupBonusTier },
    errors: [],
    emits: ["topup_bonus_tier.changed"],
  }),
  "operator.wallet.get": defineRoute({
    ...wallet,
    id: "operator.wallet.get",
    method: "get",
    path: `${op}/companies/{company_id}/wallet`,
    summary: "A company's wallet with our cost and margin, its tariffs and recent entries",
    access: "operator",
    params: OperatorCompanyParams,
    response: { status: 200, schema: OperatorWalletView },
    errors: [],
    emits: [],
  }),
  "operator.wallet.putSettings": defineRoute({
    ...wallet,
    id: "operator.wallet.putSettings",
    method: "put",
    path: `${op}/companies/{company_id}/wallet/settings`,
    summary: "Switch a company's wallet on or off and set its top-up limits",
    access: "operator",
    params: OperatorCompanyParams,
    body: OperatorWalletSettingsPut,
    response: { status: 200, schema: OperatorWalletView },
    errors: ["conflict"],
    emits: ["wallet.settings_changed", "wallet.low", "tariff.changed"],
  }),
  "operator.wallet.adjust": defineRoute({
    ...wallet,
    id: "operator.wallet.adjust",
    method: "post",
    path: `${op}/companies/{company_id}/wallet/adjustments`,
    summary: "Credit or debit a company's wallet by hand, with a reason",
    access: "operator",
    params: OperatorCompanyParams,
    body: WalletAdjustmentCreate,
    idempotency: "required",
    response: { status: 201, schema: OperatorWalletEntry },
    errors: ["wallet_off", "wallet_empty"],
    emits: ["wallet.adjusted", "wallet.low", "wallet.empty"],
  }),
  "operator.wallet.refund": defineRoute({
    ...wallet,
    id: "operator.wallet.refund",
    method: "post",
    path: `${op}/companies/{company_id}/wallet/refunds`,
    summary: "Refund part of a paid top-up to the card (never more than the balance)",
    access: "operator",
    params: OperatorCompanyParams,
    body: WalletRefundCreate,
    idempotency: "required",
    response: { status: 201, schema: OperatorWalletEntry },
    errors: ["wallet_empty", "not_ready", "conflict"],
    emits: ["wallet.refunded", "wallet.low", "wallet.empty"],
  }),
  "operator.wallet.ledgerTopUp": defineRoute({
    ...wallet,
    id: "operator.wallet.ledgerTopUp",
    method: "post",
    path: `${op}/companies/{company_id}/wallet/ledger-top-ups`,
    summary:
      "A top-up paid outside a card checkout, on the ledger (BILLING-LEDGER): credited with its bonus",
    access: "operator",
    params: OperatorCompanyParams,
    body: WalletLedgerTopUp,
    idempotency: "optional",
    response: { status: 201, schema: OperatorWalletView },
    errors: ["wallet_off", "not_ready", "conflict"],
    emits: ["wallet.checkout_started", "wallet.credited"],
  }),
  "operator.wallet.createTariff": defineRoute({
    ...wallet,
    id: "operator.wallet.createTariff",
    method: "post",
    path: `${op}/companies/{company_id}/wallet/tariff`,
    summary: "Custom prices for one company (a new version of its override)",
    access: "operator",
    params: OperatorCompanyParams,
    body: TariffCreate,
    idempotency: "optional",
    response: { status: 201, schema: Tariff },
    errors: [],
    emits: ["tariff.changed"],
  }),
  "operator.wallet.retireTariff": defineRoute({
    ...wallet,
    id: "operator.wallet.retireTariff",
    method: "delete",
    path: `${op}/companies/{company_id}/wallet/tariff/{tariff_id}`,
    summary: "Retire a company's custom price version that is not in effect yet",
    access: "operator",
    params: CompanyTariffParams,
    response: { status: 200, schema: Tariff },
    errors: ["conflict"],
    emits: ["tariff.changed"],
  }),
  "operator.walletMargin.get": defineRoute({
    ...wallet,
    id: "operator.walletMargin.get",
    method: "get",
    path: `${op}/wallet-margin`,
    summary: "Charged, cost and margin per company, agent and task for a month",
    access: "operator",
    query: WalletMarginQuery,
    response: { status: 200, schema: WalletMargin },
    errors: [],
    emits: [],
  }),
} as const;
